import { supabase } from '../lib/supabase';
import type { Profile } from '../types';

// Clerk user type - defining inline to avoid import issues
interface ClerkUser {
  id: string;
  username: string | null;
  fullName: string | null;
  imageUrl: string;
  primaryEmailAddress?: { emailAddress: string } | null;
}

export interface AuthUser {
  id: string;
  email: string;
  profile?: Profile;
}

/**
 * Sync Clerk user to Supabase profiles table
 * This is called automatically when a user signs in via Clerk
 */
export async function syncClerkUserToSupabase(clerkUser: ClerkUser): Promise<void> {
  if (!supabase) {
    console.warn('Supabase not configured, skipping user sync');
    return;
  }

  try {
    console.log('[Auth] Syncing Clerk user to Supabase:', clerkUser.id);

    const profile = {
      // Don't set 'id' - let Supabase auto-generate UUID
      clerk_user_id: clerkUser.id,
      username: clerkUser.username || null,
      display_name: clerkUser.fullName || null,
      avatar_url: clerkUser.imageUrl || null,
      is_active: true,
      last_login_at: new Date().toISOString(),
      // Don't set role here - it defaults to 'user' in the database
    };

    console.log('[Auth] Profile data to upsert:', profile);

    // First, try to check if profile exists
    const { data: existingProfile, error: selectError } = await supabase
      .from('profiles')
      .select('id, clerk_user_id, role')
      .eq('clerk_user_id', clerkUser.id)
      .maybeSingle();

    if (selectError) {
      console.error('[Auth] Error checking existing profile:', selectError);
    } else {
      console.log('[Auth] Existing profile:', existingProfile);
    }

    // Upsert to profiles table
    const { data, error } = await supabase
      .from('profiles')
      .upsert(profile, {
        onConflict: 'clerk_user_id',
        ignoreDuplicates: false,
      })
      .select();

    if (error) {
      console.error('[Auth] Failed to sync Clerk user to Supabase:', {
        error,
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
      });

      throw new Error('Database error saving user profile: ' + error.message);
    }

    console.log('[Auth] Successfully synced Clerk user to Supabase:', {
      userId: clerkUser.id,
      data,
    });
  } catch (err) {
    console.error('[Auth] Error syncing user:', err);
    // Re-throw with more context
    if (err instanceof Error) {
      throw new Error('Failed to sync user to database: ' + err.message);
    }
    throw err;
  }
}

/**
 * Check if a user has admin role
 */
export function isAdmin(user: AuthUser | null): boolean {
  if (!user?.profile) return false;
  return user.profile.role === 'admin';
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(user: AuthUser | null): boolean {
  return user !== null;
}

/**
 * Get user profile from Supabase
 */
export async function getUserProfile(userId: string): Promise<Profile | null> {
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('clerk_user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    return data;
  } catch (err) {
    console.error('Error getting profile:', err);
    return null;
  }
}

/**
 * Log admin action to admin_logs table
 */
export async function logAdminAction(
  action: string,
  targetUserId: string | null = null,
  metadata: Record<string, any> = {}
): Promise<void> {
  if (!supabase) return;

  try {
    const { error } = await supabase
      .from('admin_logs')
      .insert({
        action,
        target_user_id: targetUserId,
        metadata,
      });

    if (error) {
      console.error('Failed to log admin action:', error);
    }
  } catch (err) {
    console.error('Error logging admin action:', err);
  }
}

